import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { Avatar, Chip, Button, Grid, Slide, useScrollTrigger } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { IconSearch, IconRefresh, IconCheck } from '@tabler/icons';
import { SparksIcon } from '#root/icons';

import { useDialog, useResponsive, useAuth, usePaginatedListWithOffset } from '#root/hooks';
import { travelerService } from '#root/services';
import { CreateOfferDrawer, SearchDialog, SkeletonList } from '#root/components';
import { Badge as DsBadge, Button as DsButton } from '#root/components/ui';
import { GuveryLayout, GuveryPageHeader } from '#root/ui-components';
import { GuveryPaper, GuveryEmpty } from '#root/ui-components/cards';
import { ShippingSearch } from '#root/assets';
import * as ROUTES from '#root/constants/navigation';
import OrderCard from './OrderCard';
import OrdersInfoPanel from './OrdersInfoPanel';
import MatchFeed from './Match/MatchFeed';
import MatchPromoCard from './Match/MatchPromoCard';
import { isMatchNewVisible, markMatchSeen } from './Match/matchNewBadge';
import matchStyles from './Match/match.module.scss';
import styles from './travelerOrders.module.scss';

function ElevationScroll(props) {
  const { children, search } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 60,
  });

  return (
    <Slide appear={false} direction="down" in={trigger && search}>
      {children}
    </Slide>
  );
}

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

type SearchType = string | number;

function TravelerOrders(props) {
  let query = useQuery();
  let queryParamSearch: SearchType = query.get('search');
  const orderId = props.match.params.id;
  const { usuario } = useAuth();
  const { isMobile } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const ITEMS_BY_PAGE = 7;
  const [loadingPagination, setLoadingPagination] = useState(false);
  const [paginable, setPaginable] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [orderObj, setOrderObj] = useState(null);
  const [search, setSearch] = useState<SearchType>(queryParamSearch);
  const searchDialog = useDialog();
  const history = useHistory();
  // Vista Todos | Match por `?tab=match`. El detalle de un pedido (`/:id`) es siempre "Todos".
  const view: 'all' | 'match' = !orderId && query.get('tab') === 'match' ? 'match' : 'all';
  const hasFetchedOrders = useRef(false);
  // Chip "Nuevo" del toggle: se calcula una vez, así sigue visible durante esta visita aunque
  // ya se haya marcado como visto
  const [showNew] = useState(isMatchNewVisible);

  useEffect(() => {
    if (view === 'match') markMatchSeen();
  }, [view]);

  const goToMatch = () => {
    history.push({ pathname: ROUTES.TRAVELER_ORDERS, search: '?tab=match' });
    window.scrollTo(0, 0);
  };

  const goToAll = () => {
    history.push({
      pathname: ROUTES.TRAVELER_ORDERS,
      search: search ? `?search=${search}` : '',
    });
    window.scrollTo(0, 0);
  };

  const handleOpenTravelerOffer = (order) => {
    setOrderObj(order);
    // `true` explicito, no toggle: con el drawer ya abierto, hacer clic en otro
    // pedido lo cerraba en vez de cambiar de pedido.
    setDrawerOpen(true);
  };

  const handleOrdersReset = () => {
    history.push({
      pathname: ROUTES.TRAVELER_ORDERS,
    });
    window.scrollTo(0, 0);
    setSearch('');
    if (offset === 0) {
      return fetchOrders();
    }
    setOffset(0);
  };

  const fetchOrders = async () => {
    hasFetchedOrders.current = true;
    if (offset > 0) setLoadingPagination(true);
    setLoading(true);
    setPaginable(true);
    if (search) {
      history.push({
        pathname: ROUTES.TRAVELER_ORDERS,
        search: `?search=${search}`,
      });
    } else {
      history.push({
        pathname: ROUTES.TRAVELER_ORDERS,
      });
    }
    const res = (!search) 
    ? await travelerService.orders(offset)
    : await travelerService.ordersSearch(search, offset);
    setLoadingPagination(false);
    setLoading(false);
    if (res.error) return;
    if (offset > 0) {
      setOrders(prevOrders => [...prevOrders, ...res.data]);
    } else {
      setOrders(res.data);
    }
    setHasMore(res.data.length > (ITEMS_BY_PAGE - 1));
  };

  const fetchOrderById = async (id) => {
    setLoading(true);
    setPaginable(false);
    setOrders([]);
    const res = await travelerService.orderById(id);
    if (!res.error) {
      if (res.data) {
        setOrders([res.data]);
      }
    }
    setLoading(false);
  };

  // Remover pedido de la lista (inmutable)
  const removeOrder = useCallback((order) => {
    setOrders((prevOrders) => prevOrders.filter((o) => o.idp !== order.idp));
  }, []);

  // Restaurar pedido en caso de rollback (para optimistic UI)
  const restoreOrder = useCallback((order) => {
    setOrders((prevOrders) => {
      // Evitar duplicados
      if (prevOrders.some((o) => o.idp === order.idp)) {
        console.warn('Order already exists in list, skipping restore');
        return prevOrders;
      }
      // Re-agregar al inicio con flag temporal para animación
      const restoredOrder = { ...order, _justRestored: true };

      // Remover flag después de 3s (tiempo de animación)
      setTimeout(() => {
        setOrders((currentOrders) =>
          currentOrders.map((o) =>
            o.idp === order.idp ? { ...o, _justRestored: false } : o
          )
        );
      }, 3000);

      return [restoredOrder, ...prevOrders];
    });
  }, []);

  // Hook de paginación con IntersectionObserver
  const { lastElementRef: lastMapElementRef } = usePaginatedListWithOffset(
    loading,
    hasMore,
    offset,
    setOffset,
    ITEMS_BY_PAGE,
    { enabled: paginable && view === 'all' }
  );

  // useEffect para carga inicial (mount). En Match no se llama `fetchOrders`: su
  // `history.push` sin query borraría `?tab=match`.
  useEffect(() => {
    if (orderId) {
      fetchOrderById(orderId);
      return;
    }
    if (view === 'all') fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Entrada directa por `?tab=match` y luego "Todos": la lista aún no se cargó
  useEffect(() => {
    if (orderId || view !== 'all' || hasFetchedOrders.current) return;
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // useEffect para paginación y búsqueda
  useEffect(() => {
    if (orderId) return;
    // Solo fetch cuando offset > 0 (paginación) o cuando search cambia
    if (offset > 0 || search !== null) {
      fetchOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, search]);

  const headerExtra = [
    <Fragment key="0">
      {!search && !orderId && view === 'all' && (
        <DsButton
          variant="outline"
          onClick={searchDialog.openDialog}
          icon={<IconSearch size={18} />}>
          Buscar
        </DsButton>
      )}
    </Fragment>,
    <Fragment key="1">
      {search && view === 'all' && (
        <Chip
          avatar={
            <Avatar>
              <IconSearch size={16} />
            </Avatar>
          }
          label={search}
          onDelete={handleOrdersReset}
        />
      )}
    </Fragment>,
    <Fragment key="2">
      {orderId && (
        <Chip
          avatar={
            <Avatar>
              <IconSearch size={16} />
            </Avatar>
          }
          label={`Pedido: #${orderId}`}
          onDelete={handleOrdersReset}
        />
      )}
    </Fragment>,
  ];

  const OrderList = ({ orders, disableOffer, handleOpenOffer }) => (
    <>
      {orders?.length < 1 ? (
        <Grid item xs={12}>
          <GuveryEmpty description="No se encontraron pedidos..." image={ShippingSearch}>
            {search && (
              <DsButton variant="outline" onClick={handleOrdersReset}>
                Ver todos
              </DsButton>
            )}
          </GuveryEmpty>
        </Grid>
      ) : (
        <>
          {orders.map((order, index) => {
            const isRestored = order._justRestored;
            if (orders.length === index + 1) {
              return (
              <Grid item xs={12} key={index} className={isRestored ? styles.orderRestored : ''}>
              <OrderCard
                xref={lastMapElementRef}
                order={order}
                disableOffer={disableOffer}
                onActionClick={handleOpenOffer}
              />
            </Grid>)
            } else {
              return (
              <Grid item xs={12} key={index} className={isRestored ? styles.orderRestored : ''}>
              <OrderCard
                order={order}
                disableOffer={disableOffer}
                onActionClick={handleOpenOffer}
              />
            </Grid>)
            }
          })}
          
          {paginable && loadingPagination && (
            <Grid item xs={12}>
            <GuveryPaper disableBorder disableElevation disableHover className={styles.root}>
            <h3 className="guv-text-alt-2" style={{textAlign: 'center', marginBottom: 30}}>
              <IconRefresh size={18} />{'  '}Cargando...
            </h3>
            </GuveryPaper>
            </Grid>
          )}

          {paginable && !loadingPagination && !hasMore && (
            <Grid item xs={12}>
            <GuveryPaper disableBorder disableElevation disableHover className={styles.root}>
            <h3 className="guv-text-alt-2" style={{textAlign: 'center', marginBottom: 30}}>
              <IconCheck size={18} />{'  '}Revisaste todos los pedidos...
            </h3>
            </GuveryPaper>
            </Grid>
          )}

        </>
      )}
      {orderId && (
        <Grid item xs={12}>
          <DsButton fullWidth size="lg" variant="outline" onClick={handleOrdersReset}>
            Ver todos los pedidos
          </DsButton>
        </Grid>
      )}
    </>
  );

  const alert = (
    <div className={styles.alert}>
      <RouterLink to={ROUTES.TRAVELER_TRAINING} className="guv-no-decoration">
        <Alert
          severity="error"
          action={
            !isMobile && (
              <Button color="inherit" variant="outlined" size="small">
                Capacitarme
              </Button>
            )
          }>
          <h5>Capacitación Obligatoria!</h5>
          Sólo los viajeros capacitados pueden hacer ofertas.
        </Alert>
      </RouterLink>
    </div>
  );

  return (
    <GuveryLayout style={{ position: 'relative' }}>
      {!usuario.capable_traveler_at && alert}
      <ElevationScroll search={view === 'all' && search}>
        <div className={styles.searchTopbar}>
          <div className={styles.wrap}>
            <div className={styles.flex}>
              <h4 className={styles.title}>Buscando:</h4>
              <Chip
                avatar={
                  <Avatar>
                    <IconSearch size={16} />
                  </Avatar>
                }
                label={search}
                onDelete={handleOrdersReset}
              />
            </div>
          </div>
        </div>
      </ElevationScroll>
      <GuveryPageHeader title="Pedidos" extra={headerExtra} />
      <div className={styles.infoPanel}>
        <OrdersInfoPanel view={view} />
      </div>
      {!orderId && (
        <div className={matchStyles.toggle} role="group" aria-label="Vista de pedidos">
          <DsButton
            size="sm"
            variant={view === 'all' ? 'primary' : 'outline'}
            aria-pressed={view === 'all'}
            onClick={goToAll}>
            Todos
          </DsButton>
          <div className={matchStyles.matchTab}>
            <DsButton
              size="sm"
              variant={view === 'match' ? 'match' : 'matchOutline'}
              aria-pressed={view === 'match'}
              icon={<SparksIcon fontSize="small" />}
              onClick={goToMatch}>
              Match
            </DsButton>
            {showNew && (
              <DsBadge size="sm" variant="success" className={matchStyles.newChip}>
                Nuevo
              </DsBadge>
            )}
          </div>
        </div>
      )}
      {view === 'match' ? (
        <MatchFeed onGoToAll={goToAll} />
      ) : (
        <>
        {/* Fuera del Grid: sin card no queda un ítem vacío con su espaciado */}
        {!search && !orderId && (
          <MatchPromoCard className={styles.matchPromo} onOpen={goToMatch} />
        )}
        <Grid container spacing={3}>
          {(!loading || loadingPagination) ? (
            <>
            <OrderList
              orders={orders}
              disableOffer={usuario.capable_traveler_at ? false : true}
              handleOpenOffer={handleOpenTravelerOffer}
            />
            {loadingPagination ?? (
              <h1>Cargando...</h1>
            )}
            </>
          ) : (
            <SkeletonList count={6} height={240} paperProps={{ disableBorder: true, disableHover: true }} />
          )}
        </Grid>
        </>
      )}
      {orderObj && (
        <CreateOfferDrawer
          // El drawer nunca se desmonta (`orderObj` jamas vuelve a null), asi que
          // sin `key` conservaria el formulario de un pedido al siguiente.
          key={orderObj.idp}
          order={orderObj}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          removeOrder={removeOrder}
          restoreOrder={restoreOrder}
        />
      )}
      <SearchDialog
        open={searchDialog.open}
        onClose={searchDialog.closeDialog}
        searchBy="comprador, título, link o categoría"
        setSearch={setSearch}
        setOffset={setOffset}
        clearSearchOnOpen
      />
    </GuveryLayout>
  );
}

export default TravelerOrders;
